import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAgent247BillingStatus,
  createAgent247BillingCheckoutSession,
} from "../../utils/api";
// Reuses the shared .ai-plan-price / .aca-plan-price-suffix / .aca-plan-breakdown
// styles defined for the AI Call Assist product cards, for visual consistency.
import "../aiCallAssist/AICallAssistPlanSelector.css";

// Copy only — pricing tier itself always comes from the server
// (Agency.agent247MembershipStatus, Super-Admin controlled). Exactly one of
// these two cards is ever shown, never both, per the Member/Non-Member gate.
const TIER_COPY = {
  member: {
    title: "24/7 AI Call Assist",
    subtitle:
      "Member pricing — includes a one-time setup fee, the monthly call assist plan, and the flat platform usage fee, billed together via Stripe Checkout.",
  },
  non_member: {
    title: "24/7 AI Call Assist — Non-Member",
    subtitle:
      "Non-Member pricing — includes a one-time setup fee, the monthly call assist plan, and the flat platform usage fee, billed together via Stripe Checkout.",
  },
};

const ACTIVE_STATUS_LABEL = {
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  pending: "Pending",
};

// Amounts arrive from the backend in cents (live from Stripe, never
// hardcoded) — format only, never compute/guess a dollar figure here.
const formatCents = (cents) => {
  if (typeof cents !== "number" || !Number.isFinite(cents)) return null;
  const hasFraction = cents % 100 !== 0;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const buildBreakdownLine = (pricing) => {
  const parts = [];
  const setupFee = formatCents(pricing?.setupFeeAmount);
  if (setupFee) parts.push(`${setupFee} one-time setup fee`);
  const platformFee = formatCents(pricing?.platformFeeAmount);
  if (platformFee) parts.push(`${platformFee}/mo platform usage fee`);
  return parts.join(" + ");
};

const Agent247BillingCard = ({ locationId }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Only relevant BEFORE checkout — once a subscription is active, the
  // billing cadence is fixed and comes from the server (status.billingInterval),
  // not from this local toggle.
  const [billingIntervalChoice, setBillingIntervalChoice] = useState("monthly");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchAgent247BillingStatus({ locationId });
      setStatus(payload);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load Agent247 billing status.",
      );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search || "");
    if (!params.get("billing_success")) return;

    setSuccess("Payment successful! Your Agent247 subscription is now active.");
    void loadStatus();

    params.delete("billing_success");
    const nextQuery = params.toString();
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`,
    );
  }, [loadStatus]);

  const handleSubscribe = async () => {
    setPending(true);
    setError("");
    try {
      const result = await createAgent247BillingCheckoutSession({
        locationId,
        billingInterval: billingIntervalChoice,
      });
      const checkoutUrl = String(result?.url || "").trim();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      throw new Error("Checkout session did not return a redirect URL.");
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to start checkout.",
      );
    } finally {
      setPending(false);
    }
  };

  // Hide entirely while loading, outside the pilot, or if the agency has
  // never been provisioned for this feature — matches the AI Call Assist
  // (product) section's "invisible until enabled" gating convention.
  if (loading) return null;
  if (!status?.pilotEnabled) return null;

  const tier = status.agent247MembershipStatus === "member" ? "member" : "non_member";
  const copy = TIER_COPY[tier];
  const isActive = status.status === "active";
  const pricing = status.pricing || {};
  // Once active, the toggle is gone and the card reflects whatever the
  // agency actually purchased (status.billingInterval, server-derived);
  // before that, it reflects the customer's in-progress selection.
  const displayInterval = isActive
    ? status.billingInterval === "annual"
      ? "annual"
      : "monthly"
    : billingIntervalChoice;
  const isAnnualDisplay = displayInterval === "annual";
  const callAssistPrice = formatCents(
    isAnnualDisplay ? pricing.callAssistAnnualAmount : pricing.callAssistAmount,
  );
  const callAssistInterval = isAnnualDisplay
    ? pricing.callAssistAnnualInterval || "year"
    : pricing.callAssistInterval || "month";
  const breakdownLine = buildBreakdownLine(pricing);

  return (
    <section className="pb-section">
      <h2 className="pb-section-title">Agent247 — 24/7 AI Call Assist</h2>
      <div className="ai-plan-selector-wrap">
        {success ? <div className="ai-alert ai-alert-success">{success}</div> : null}
        {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

        <div className="ai-card ai-plan-selector-card">
          <div className="ai-card-header">
            <div>
              <p className="ai-card-note">Membership tier: {tier === "member" ? "Member" : "Non-Member"}</p>
              <h3 className="ai-card-title">{copy.title}</h3>
            </div>
          </div>

          <div className="ai-plan-item">
            {!isActive ? (
              <div className="agent247-billing-interval-toggle" role="group" aria-label="Billing interval">
                <button
                  type="button"
                  className={`ai-button ai-button-toggle${
                    billingIntervalChoice === "monthly" ? " ai-button-toggle-active" : ""
                  }`}
                  onClick={() => setBillingIntervalChoice("monthly")}
                  disabled={pending}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`ai-button ai-button-toggle${
                    billingIntervalChoice === "annual" ? " ai-button-toggle-active" : ""
                  }`}
                  onClick={() => setBillingIntervalChoice("annual")}
                  disabled={pending}
                >
                  Annual <span className="agent247-billing-discount-badge">10% off</span>
                </button>
              </div>
            ) : null}
            <span className="ai-plan-badge">
              /{callAssistInterval} + setup{isAnnualDisplay ? " (annual)" : ""}
            </span>
            {callAssistPrice ? (
              <p className="ai-plan-price">
                {callAssistPrice}
                <span className="aca-plan-price-suffix">/ {callAssistInterval}</span>
              </p>
            ) : null}
            {breakdownLine ? <p className="aca-plan-breakdown">+ {breakdownLine}</p> : null}
            <p className="ai-help">{copy.subtitle}</p>

            {isActive ? (
              <button type="button" className="ai-button ai-button-disabled" disabled>
                ✓ {ACTIVE_STATUS_LABEL[status.status] || "Active"}
              </button>
            ) : (
              <button
                type="button"
                className="ai-button ai-button-primary"
                onClick={handleSubscribe}
                disabled={pending}
              >
                {pending ? (
                  <>
                    <span className="ai-spinner" aria-hidden="true" />
                    Processing...
                  </>
                ) : (
                  "Subscribe"
                )}
              </button>
            )}
            {!isActive && status.status && status.status !== "pending" ? (
              <p className="ai-help">
                Subscription status: {ACTIVE_STATUS_LABEL[status.status] || status.status}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Agent247BillingCard;
